import api from '@/api/api';
import { useApiGet } from '@/api/apiRequests';
import { getContexts, getOrps } from '@/api/services';
import { PageLoader } from '@/components/PageLoader';
import { ContextItem } from '@/interfaces/Contexts';
import { OrpItem } from '@/interfaces/Orps.ts';
import { AxiosError } from 'axios';
import { EditIcon, Trash2Icon } from 'lucide-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export const Contexts: FC = () => {
  const [tableData, setTableData] = useState();
  const [orps, setOrps] = useState<OrpItem[] | undefined>();
  const [itemDialog, setItemDialog] = useState<boolean>(false);
  const emptyItem: ContextItem = {
    // biome-ignore lint/style/useNamingConvention: <explanation>
    context_type: '',
    year: 2024,
    region: '',
    orp: '',
    ico: '',
    context: '',
    id: 0,
  };
  const [item, setItem] = useState<ContextItem>(emptyItem);
  const [yearFilter, setYearFilter] = useState<string | null>(null);
  const [orpFilter, setOrpFilter] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
  const [contextToDelete, setContextToDelete] = useState<number | null>(null);
  const { data, isLoading, refetch } = useApiGet<any, AxiosError>(['contexts'], async () => await getContexts(), {
    enabled: true,
  });
  const { data: orpsData } = useApiGet<OrpItem[], AxiosError>(['orps'], async () => await getOrps(), {
    enabled: true,
  });

  useEffect(() => {
    if (data) {
      if (yearFilter) {
        // Filter data by selected year
        setTableData(data.filter((item: any) => item.year === yearFilter));
      } else if (orpFilter) {
        // Filter data by selected orp
        setTableData(data.filter((item: any) => item.orp === orpFilter));
      } else {
        // No filter, show all data
        setTableData(data);
      }
    }
  }, [data, yearFilter, orpFilter]);

  useEffect(() => {
    setOrps(orpsData);
  }, [orpsData]);

  const handleYearClick = (year: string) => {
    setYearFilter((prevFilter) => (prevFilter === year ? null : year));
  };

  const handleOrpClick = (orp: string) => {
    setOrpFilter((prevFilter) => (prevFilter === orp ? null : orp));
  };

  const openNew = () => {
    setItem(emptyItem);
    setIsEditMode(false);
    setItemDialog(true);
  };

  const openEdit = (rowData: ContextItem) => {
    setItem({
      // biome-ignore lint/style/useNamingConvention: <explanation>
      context_type: rowData.context_type,
      year: rowData.year,
      region: rowData.region,
      orp: rowData.orp,
      ico: rowData.ico,
      context: rowData.context,
      id: rowData.id,
    });
    setIsEditMode(true);
    setItemDialog(true);
  };

  const hideDialog = () => {
    setItem(emptyItem);
    setIsEditMode(false);
    setItemDialog(false);
  };

  const saveItem = async () => {
    // if (!item.name || !item.category || !item.input_type) {
    //   toast.error('Fill in all fields.');
    //   return;
    // }
    //
    try {
      let response;

      if (isEditMode && item.id) {
        // Update existing context
        response = await api.put(`/contexts/${item.id}`, {
          context: item.context,
        });
      } else {
        // Create new context
        response = await api.post(`/contexts`, {
          // biome-ignore lint/style/useNamingConvention: <explanation>
          context_type: item.context_type,
          year: item.year,
          region: item.region || null,
          ico: item.ico || null,
          context: item.context,
        });
      }

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        toast.success(isEditMode ? 'The record has been successfully edited.' : 'The record was successfully saved.');
        await refetch();
        setItemDialog(false);
        setItem(emptyItem);
        setIsEditMode(false);
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error when saving a record.');
      }
      console.error('Error saving item:', error);
    }
  };

  const itemDialogFooter = (
    <>
      <Button className={'btn-secondary'} style={{ borderRadius: '6px' }} label="Cancel" outlined={true} onClick={hideDialog} />
      <Button className={'btn-primary'} style={{ borderRadius: '6px' }} label="Save" onClick={saveItem} />
    </>
  );

  const confirmDelete = (itemId: number) => {
    setContextToDelete(itemId);
    setDeleteDialogVisible(true);
  };

  const handleDeleteContext = async () => {
    if (!contextToDelete) return;

    try {
      const response = await api.delete(`/contexts/${contextToDelete}`);

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        toast.success('Context has been successfully deleted.');
        await refetch();
        setDeleteDialogVisible(false);
        setContextToDelete(null);
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error when deleting a record.');
      }
      console.error('Error deleting item:', error);
    }
  };

  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="flex gap-1">
        <Button
          icon={<EditIcon size={16} />}
          className="ml-auto p-button-text p-button-info"
          onClick={() => openEdit(rowData)}
        />
        <Button
          icon={<Trash2Icon size={16} />}
          className="ml-auto p-button-text p-button-danger"
          onClick={() => confirmDelete(rowData.id)}
        />
      </div>
    );
  };

  const getUniqueOptions = (data: any[], field: string) => {
    return data.reduce((acc, current) => {
      const value = field.includes('.') 
        ? field.split('.').reduce((obj: any, key: string) => obj?.[key], current)
        : current[field];
      if (value && !acc.find((item: any) => {
        const itemValue = field.includes('.')
          ? field.split('.').reduce((obj: any, key: string) => obj?.[key], item)
          : item[field];
        return itemValue === value;
      })) {
        acc.push(current);
      }
      return acc;
    }, []);
  };

  // Transform ORPs for dropdown - extract municipality name
  const orpOptions = orps?.map((orp) => ({
    ...orp,
    label: orp.municipality?.name || '',
    value: orp.municipality?.name || '',
  })) || [];

  // Transform regions for dropdown - extract unique regions
  const regionOptions = getUniqueOptions(orps || [], 'municipality.region').map((item: any) => ({
    ...item,
    label: item.municipality?.region || '',
    value: item.municipality?.region || '',
  }));

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2018; year <= currentYear; year++) {
      years.push({ label: year.toString(), value: year });
    }
    return years;
  };

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Contexts</h1>
      <div className={'flex justify-end gap-2 mb-2'}>
        {yearFilter && (
          <Button
            label={`Clear filter: ${yearFilter}`}
            size={'small'}
            severity={'danger'}
            outlined={true}
            style={{ borderRadius: '6px' }}
            onClick={() => setYearFilter(null)}
          />
        )}
        {orpFilter && (
          <Button
            label={`Clear filter: ${orpFilter}`}
            size={'small'}
            severity={'danger'}
            outlined={true}
            style={{ borderRadius: '6px' }}
            onClick={() => setOrpFilter(null)}
          />
        )}
        <Button label="Add context" className={'btn-primary'} style={{ borderRadius: '6px' }} onClick={openNew} />
      </div>

      <DataTable
        value={tableData}
        removableSort={true}
        showGridlines={true}
        paginator={true}
        size={'small'}
        sortField={'id'}
        sortOrder={1}
        rows={20}
        tableStyle={{ minWidth: '50rem', width: '100%' }}
      >
        <Column field="context_type" header="Context type" sortable={true} style={{ width: '10rem' }} />
        <Column
          field="year"
          header="Year"
          sortable={true}
          body={(rowData: any) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <span
              className="cursor-pointer hover:text-blue-600 hover:underline"
              onClick={() => handleYearClick(rowData.year)}
            >
              {rowData.year}
            </span>
          )}
          style={{ width: '5.5rem' }}
        />
        <Column field="region" header="Region" sortable={true} style={{ width: '13rem' }} />
        <Column
          field="orp"
          header="Orp"
          sortable={true}
          body={(rowData: any) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <span
              className="cursor-pointer hover:text-blue-600 hover:underline"
              onClick={() => handleOrpClick(rowData.orp)}
            >
              {rowData.orp}
            </span>
          )}
          style={{ width: '13rem' }}
        />
        <Column field="context" header="Context" style={{ minWidth: '13rem' }} />
        <Column align={'center'} style={{ width: '4rem' }} body={actionBodyTemplate} />
      </DataTable>

      <Dialog
        visible={itemDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header={isEditMode ? 'Update context' : 'Add new context'}
        modal={true}
        className="p-fluid"
        footer={itemDialogFooter}
        onHide={hideDialog}
        draggable={false}
      >
        <div className={'flex flex-col gap-4'}>
          <div className="field">
            <label htmlFor="context_type" className="font-bold">
              Context type
            </label>
            <Dropdown
              value={item?.context_type}
              onChange={(e: DropdownChangeEvent) => {
                if (e.value === 'region') {
                  // biome-ignore lint/style/useNamingConvention: <explanation>
                  setItem((prev) => ({ ...prev, context_type: e.value, orp: '', ico: '' }));
                } else if (e.value === 'republic') {
                  // biome-ignore lint/style/useNamingConvention: <explanation>
                  setItem((prev) => ({ ...prev, context_type: e.value, orp: '', ico: '', region: '' }));
                } else {
                  // biome-ignore lint/style/useNamingConvention: <explanation>
                  setItem((prev) => ({ ...prev, context_type: e.value }));
                }
              }}
              options={['republic', 'region', 'orp']}
              id={'context_type'}
              optionLabel="label"
              required={true}
              placeholder="Choose context type"
              className="w-full"
              disabled={isEditMode}
            />
          </div>
          <div className="field">
            <label htmlFor="year" className="font-bold">
              Year
            </label>
            <Dropdown
              value={item?.year}
              onChange={(e: DropdownChangeEvent) => setItem((prev) => ({ ...prev, year: e.value }))}
              options={generateYearOptions()}
              optionLabel="label"
              required={true}
              placeholder="Choose year"
              className="w-full"
              disabled={isEditMode}
            />
          </div>
          <div className="field">
            <label htmlFor="orp" className="font-bold">
              Orp
            </label>
            <Dropdown
              value={item?.orp}
              id={'orp'}
              onChange={(e: DropdownChangeEvent) => {
                const selectedOrpValue = e.value as string;
                const selectedOrp = orps?.find((orp) => orp.municipality?.name === selectedOrpValue);
                setItem((prev) => ({
                  ...prev,
                  orp: selectedOrpValue,
                  ico: selectedOrp?.ico || '',
                  region: selectedOrp?.municipality?.region || prev.region,
                }));
              }}
              options={orpOptions}
              optionLabel="label"
              optionValue="value"
              filter={true}
              required={true}
              placeholder="Choose orp"
              disabled={isEditMode || item?.context_type === 'republic' || item?.context_type === 'region'}
              className="w-full"
            />
          </div>
          <div className="field">
            <label htmlFor="region" className="font-bold">
              Region
            </label>
            <Dropdown
              value={item?.region}
              id={'region'}
              onChange={(e: DropdownChangeEvent) => setItem((prev) => ({ ...prev, region: e.value }))}
              options={regionOptions}
              optionLabel="label"
              optionValue="value"
              filter={true}
              required={true}
              placeholder="Choose region"
              disabled={isEditMode || item?.context_type === 'republic' || item?.orp !== ''}
              className="w-full"
            />
          </div>
          <div className="field">
            <label htmlFor="context" className="font-bold">
              Context
            </label>
            <InputTextarea
              id={'context'}
              value={item?.context}
              onChange={(e) => setItem((prev) => ({ ...prev, context: e.target.value }))}
              required={true}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={deleteDialogVisible}
        style={{ width: '450px' }}
        header="Confirm deletion"
        modal={true}
        draggable={false}
        footer={
          <>
            <Button label="No" className={'btn-secondary'} style={{ borderRadius: '6px' }} onClick={() => setDeleteDialogVisible(false)} />
            <Button label="Yes" onClick={handleDeleteContext} className="btn-primary" style={{ borderRadius: '6px' }} />
          </>
        }
        onHide={() => setDeleteDialogVisible(false)}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Do you really want to delete this context?</span>
        </div>
      </Dialog>
      <PageLoader visible={isLoading} />
    </>
  );
};
